/**
 * Navvis Z-Coordinate Utility for Frontend
 * 
 * This utility provides functionality to calculate Z-coordinates for assets
 * based on their X, Y coordinates and map level using the Navvis JSON data.
 * 
 * Usage:
 *   import { getZCoordinateFromNavvis } from './utils/navvisZCoordinate'
 *   
 *   const zco = await getZCoordinateFromNavvis(x, y, level)
 */

/**
 * Get the base URL for API calls
 * @returns {string} Base URL for the API
 */
function getBaseUrl() {
  const hostname = window.location.hostname;
  return hostname === 'localhost' 
    ? `http://${hostname}:9204` 
    : `https://${hostname}`;
}

/**
 * Get Z-coordinate from Navvis service based on X, Y coordinates and level/map
 * 
 * @param {number} x - X coordinate (xco)
 * @param {number} y - Y coordinate (yco)
 * @param {number|string} level - Map level number (can be numeric or string like "1", "M1", etc.)
 * @param {Object} options - Optional configuration
 * @param {number} options.offset - Z offset from floor (default: 1)
 * @param {number} options.mapHeight - Map height for Y-axis inversion (default: 6144)
 * @returns {Promise<Object>} Promise that resolves to { zco: number, found: boolean, ... } or null on error
 * 
 * @example
 * // Basic usage
 * const result = await getZCoordinateFromNavvis(5400, 4900, 1)
 * if (result && result.found) {
 *   console.log('Z-coordinate:', result.zco)
 * }
 * 
 * @example
 * // With options
 * const result = await getZCoordinateFromNavvis(5400, 4900, 'M2', { offset: 1.5 })
 */
export async function getZCoordinateFromNavvis(x, y, level, options = {}) {
  const { offset = 1, mapHeight = 6144 } = options;
  
  // Validate inputs
  if (x === undefined || x === null || isNaN(parseFloat(x))) {
    console.error('getZCoordinateFromNavvis: Invalid X coordinate:', x);
    return null;
  }
  
  if (y === undefined || y === null || isNaN(parseFloat(y))) {
    console.error('getZCoordinateFromNavvis: Invalid Y coordinate:', y);
    return null;
  }
  
  if (level === undefined || level === null) {
    console.error('getZCoordinateFromNavvis: Invalid level:', level);
    return null;
  }
  
  // Extract numeric level from string formats like "M1", "M2", etc.
  let numericLevel = level;
  if (typeof level === 'string') {
    // Handle "M1", "M2" format
    const mMatch = level.match(/^M(\d+)$/);
    if (mMatch) {
      numericLevel = parseInt(mMatch[1]);
    } else if (/^\d+$/.test(level)) {
      // Pure numeric string
      numericLevel = parseInt(level);
    } else {
      console.error('getZCoordinateFromNavvis: Invalid level format:', level);
      return null;
    }
  } else {
    numericLevel = parseInt(level);
  }
  
  if (isNaN(numericLevel)) {
    console.error('getZCoordinateFromNavvis: Could not parse level:', level);
    return null;
  }
  
  const baseUrl = getBaseUrl();
  
  try {
    // Call the navvis service via direct API route
    const response = await fetch(`${baseUrl}/api/navvis/get_zco`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include', // Include cookies for authentication
      body: JSON.stringify({
        x: parseFloat(x),
        y: parseFloat(y),
        level: numericLevel,
        offset: parseFloat(offset),
        mapHeight: parseFloat(mapHeight)
      })
    });
    
    if (!response.ok) {
      console.error('getZCoordinateFromNavvis: HTTP error:', response.status, response.statusText);
      return null;
    }
    
    const result = await response.json();
    
    if (!result.ok) {
      console.error('getZCoordinateFromNavvis: Service error:', result.why, result.details);
      return null;
    }
    
    if (!result.found) {
      console.warn('getZCoordinateFromNavvis: No navvis section found for coordinates:', { x, y, level: numericLevel });
      return {
        found: false,
        zco: null,
        why: result.why,
        details: result.details
      };
    }
    
    return {
      found: true,
      zco: result.zco,
      floor_z: result.floor_z,
      ceiling_z: result.ceiling_z,
      section_id: result.section_id,
      building: result.building,
      map: result.map,
      offset: result.offset,
      details: result.details
    };
    
  } catch (error) {
    console.error('getZCoordinateFromNavvis: Network error:', error);
    return null;
  }
}

/**
 * Calculate Z-coordinate for an asset when level changes
 * This is a convenience function specifically for asset editing scenarios
 * 
 * @param {Object} asset - Asset object with xco, yco, and map properties
 * @param {number|string} newLevel - New level/map value
 * @param {Object} options - Optional configuration
 * @returns {Promise<number|null>} Promise that resolves to Z coordinate or null
 * 
 * @example
 * // In asset edit component when level changes
 * const asset = { xco: 5400, yco: 4900, map: 1 }
 * const newZco = await calculateZForAssetOnLevelChange(asset, 2)
 * if (newZco !== null) {
 *   asset.zco = newZco
 * }
 */
export async function calculateZForAssetOnLevelChange(asset, newLevel, options = {}) {
  if (!asset || asset.xco === undefined || asset.yco === undefined) {
    console.error('calculateZForAssetOnLevelChange: Invalid asset object:', asset);
    return null;
  }
  
  const result = await getZCoordinateFromNavvis(asset.xco, asset.yco, newLevel, options);
  
  if (result && result.found) {
    return result.zco;
  }
  
  return null;
}

// Make functions available globally for PlantQuest components
if (typeof window !== 'undefined') {
  window.getZCoordinateFromNavvis = getZCoordinateFromNavvis;
  window.calculateZForAssetOnLevelChange = calculateZForAssetOnLevelChange;
  
  console.log('Navvis Z-coordinate utilities loaded and available globally');
}

